import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import apiClient from '../api/axiosConfig';

interface UnmappedItem {
  id: number;
  name: string;
  type: 'network' | 'group';
}

interface CreateMappingModalProps {
  item: UnmappedItem;
  onClose: (mappingCreated: boolean) => void;
  isEditing?: boolean;
  initialCountry?: string;
}

export const CreateMappingModal: React.FC<CreateMappingModalProps> = ({ item, onClose, isEditing = false, initialCountry = '' }) => {
  const [country, setCountry] = useState(initialCountry);
  const [name, setName] = useState(item.name);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<string[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);

  // Fetch all mappings and extract unique countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setIsLoadingCountries(true);
        const response = await apiClient.get('/api/mapping/all');
        const mappingsData = response.data;

        // Extract unique country values from the mappings
        const uniqueCountries = [...new Set(Object.values(mappingsData) as string[])];

        // Sort countries alphabetically
        uniqueCountries.sort();

        setCountries(uniqueCountries);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // Filter countries based on search term
  const filteredCountries = countries.filter(c => 
    c.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateMapping = async () => {
    if (!country) {
      setError('Please select or enter a country');
      return;
    }

    if (!name) {
      setError('Please enter a name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call API to create mapping
      await apiClient.post('/api/mapping/add', {
        name,
        country,
        entityId: item.id,
        entityType: item.type
      });

      onClose(true); // Close with success
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.error || 'Failed to create mapping');
      } else {
        setError('An unexpected error occurred');
      }
      setIsLoading(false);
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => onClose(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    onClick={() => onClose(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {isEditing ? 'Edit Mapping' : 'Create Mapping'}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {isEditing ? 'Edit' : 'Create'} a mapping with a name and country
                      </p>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 rounded-md bg-boundlessBlue p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-white">
                          {error}
                        </h3>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-5 sm:mt-4">
                  {/* Name input field */}
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="Enter name"
                      />
                    </div>
                  </div>

                  <div className="flex items-center mb-4">
                    <button
                      type="button"
                      onClick={() => setIsCreatingNew(false)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        !isCreatingNew
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-700 border border-gray-300'
                      }`}
                    >
                      Select Existing
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsCreatingNew(true)}
                      className={`ml-3 px-3 py-2 text-sm font-medium rounded-md ${
                        isCreatingNew
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-700 border border-gray-300'
                      }`}
                    >
                      Create New
                    </button>
                  </div>

                  {isCreatingNew ? (
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                        Country Name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="country"
                          id="country"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="Enter country name"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                        Search Countries
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="search"
                          id="search"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="Search for a country"
                        />
                      </div>
                      <div className="mt-2 max-h-60 overflow-y-auto">
                        {isLoadingCountries ? (
                          <div className="py-4 text-center">
                            <p className="text-sm text-gray-500">Loading countries...</p>
                          </div>
                        ) : (
                          <ul className="divide-y divide-gray-200">
                            {filteredCountries.map((c) => (
                              <li key={c} className="py-2">
                                <button
                                  type="button"
                                  onClick={() => setCountry(c)}
                                  className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                                    country === c
                                      ? 'bg-primary-light text-white'
                                      : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  {c}
                                </button>
                              </li>
                            ))}
                            {!isLoadingCountries && filteredCountries.length === 0 && (
                              <li className="py-2 px-3 text-sm text-gray-500">
                                {countries.length === 0 ? 'No countries available' : 'No countries match your search'}
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleCreateMapping}
                    disabled={isLoading}
                    className={`inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                      isLoading ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isLoading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Mapping' : 'Create Mapping')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onClose(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
